import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, Menu, X, Gamepad2, Wrench, ShieldCheck, PackageSearch, Newspaper, RefreshCw } from "lucide-react";
import { useCart } from "../context/CartContext";
import { Clock } from "lucide-react";
import { api } from "../lib/api";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [navOpacity, setNavOpacity] = useState(0);
  const [sales, setSales] = useState([]);
  const navigate = useNavigate();
  const { count, preOrderCount } = useCart();
  const wrapRef = useRef(null);

  useEffect(() => {
    api.get("/sales/active").then((r) => setSales(r.data || [])).catch(() => setSales([]));
  }, []);

  useEffect(() => {
    const heroHeight = window.innerHeight * 0.82;
    const fadeStart = heroHeight * 0.6;
    const fadeEnd = heroHeight;
    const onScroll = () => {
      const y = window.scrollY;
      if (y <= fadeStart) setNavOpacity(0);
      else if (y >= fadeEnd) setNavOpacity(1);
      else setNavOpacity((y - fadeStart) / (fadeEnd - fadeStart));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowSug(false);
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  useEffect(() => {
    if (!q.trim()) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/products/search`, { params: { q, limit: 5 } });
        setSuggestions(r.data);
        setShowSug(true);
      } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/search?q=${encodeURIComponent(q)}`);
      setShowSug(false);
      setOpen(false);
    }
  };

  const navLinks = [
    ...sales.map((sale) => ({ to: `/sale/${sale.slug}`, label: sale.name, icon: Clock })),
    { to: "/community/9578260f-c6dc-4b5c-b57f-3426f22ec357", label: "GTA VI", icon: Gamepad2 },
    { to: "/about", label: "About", icon: ShieldCheck },
    { to: "/shop", label: "Shop", icon: Gamepad2 },
    { to: "/repairs", label: "Repairs", icon: Wrench },
    { to: "/trade-in", label: "Trade In", icon: RefreshCw },
    { to: "/community", label: "Community", icon: Newspaper },
    { to: "/contact", label: "Contact", icon: PackageSearch },
  ];

  return (
    <header
      className="sticky top-0 z-40 border-b transition-all duration-300"
      style={{
        backdropFilter: "blur(20px)",
        backgroundColor: "rgba(10,13,31,0.15)",
        borderBottomColor: `rgba(245,197,24,${navOpacity * 0.2})`,
      }}
    >
      <div className="site-container">
        <div className="flex items-center justify-between h-16 gap-3">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
            <img src="/logo.png" alt="GamesTrade" className="h-12 w-12 object-contain" style={{ filter: "drop-shadow(0 0 4px rgba(245,197,24,0.35))" }} />
            <div className="hidden sm:block">
              <div className="font-black text-lg tracking-widest leading-none">GAMES<span className="text-gold">TRADE</span></div>
              <div className="text-[9px] uppercase tracking-[0.25em]"><span className="text-gold">You Play</span> <span className="text-white/80">We Trade</span></div>
            </div>
          </Link>

          <form onSubmit={submit} ref={wrapRef} className="hidden md:flex flex-1 max-w-sm relative">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gold" />
              <input
                data-testid="header-search-input"
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onFocus={() => suggestions.length && setShowSug(true)}
                placeholder="Search PS5, Switch, games..."
                className="w-full bg-navy-800/60 border border-gold/20 rounded-full pl-9 pr-3 py-1.5 text-sm placeholder:text-slate-500 focus:outline-none focus:border-gold/60 transition"
              />
              {showSug && suggestions.length > 0 && (
                <div className="absolute top-full mt-2 w-full bg-navy-800 border border-gold/30 rounded-lg overflow-hidden shadow-2xl z-50">
                  {suggestions.map((s) => (
                    <Link
                      key={s.id}
                      to={`/product/${s.slug || s.id}`}
                      onClick={() => setShowSug(false)}
                      className="flex items-center gap-3 p-3 hover:bg-navy-700 transition border-b border-gold/10 last:border-0"
                      data-testid="search-suggestion"
                    >
                      {s.images?.[0] && <img src={s.images[0]} alt="" className="w-10 h-10 object-cover rounded" />}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{s.name}</div>
                        <div className="text-xs text-gold">₹{s.price.toLocaleString()}</div>
                      </div>
                      <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded bg-navy-700">{s.condition}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </form>

          <nav className="hidden lg:flex items-center gap-6">
            {navLinks.map((l) => (
              <Link key={l.to} to={l.to} className="text-sm font-bold uppercase tracking-wider hover:text-gold transition" data-testid={`nav-${l.label.toLowerCase().replace(/\s/g,'-')}`}>
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link to="/cart" className="relative p-2 hover:text-gold transition" data-testid="cart-icon">
              <ShoppingBag className="w-5 h-5" />
              {(count + preOrderCount) > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-gold text-navy-900 text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center">{count + preOrderCount}</span>
              )}
            </Link>
            <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden pb-4 space-y-3 border-t border-gold/10 pt-4">
            <form onSubmit={submit} className="md:hidden relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gold" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search..."
                className="w-full bg-navy-800/60 border border-gold/20 rounded-full pl-10 pr-4 py-2 text-sm focus:outline-none focus:border-gold/60"
                data-testid="mobile-search-input"
              />
            </form>
            {navLinks.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block text-sm font-bold uppercase tracking-wider py-1">
                {l.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </header>
  );
}
